import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ORDER_STATUSES = ["Placed", "Processing", "Completed", "Cancelled"];

export default function OrderFormDialog({ open, onClose, onSaved, customerId, customers = [] }) {
  const [form, setForm] = useState({
    customer_id: customerId || "",
    amount: "",
    status: "Placed",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {

    // eslint-disable-next-line
    setForm({
      customer_id: customerId || "",
      amount: "",
      status: "Placed",
      description: "",
    });
  }, [customerId, open]);

  const submit = async () => {
    if (!form.customer_id) {
      toast.error("Customer is required");
      return;
    }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/orders", {
        customer_id: form.customer_id,
        amount: amt,
        status: form.status,
        description: form.description,
      });
      onSaved?.(data);
    } catch {
      toast.error("Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="order-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">New Order</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Create an order linked to this customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!customerId && customers.length > 0 && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Customer
              </Label>
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))}
              >
                <SelectTrigger className="mt-1.5" data-testid="order-form-customer">
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} {c.company ? `— ${c.company}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Amount (INR)
            </Label>
            <Input
              data-testid="order-form-amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0.00"
              className="mt-1.5"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger className="mt-1.5" data-testid="order-form-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Description
            </Label>
            <Textarea
              data-testid="order-form-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
              className="mt-1.5"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 h-10 rounded-md border border-slate-200 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="order-form-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-4 h-10 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
            data-testid="order-form-save"
          >
            {saving ? "Saving…" : "Create order"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
